import { App as AntApp, ConfigProvider, Layout, Spin, theme as antdTheme, ThemeConfig } from "antd";
import dayjs from "dayjs";
import "dayjs/locale/nb";
import { useCallback, useEffect, useMemo, useState } from "react";
import { BrowserRouter as Router, Redirect, Route, Switch } from "react-router-dom";

// Contexts
import { isBlMemberUser, isCompanyUser, useLoginContext } from "@bindeleddet/core/contexts/login";
import { NotificationBridge } from "@bindeleddet/core/utils";
import { ThemeContext, ThemeMode } from "contexts/theme-context";

// Components
import AdminFooter from "components/FooterAdmin/FooterAdmin";
import Navbar, { NAVBAR_HEIGHT } from "components/Nav/Navbar";
import AdminNavbar from "components/NavAdmin/AdminNavbar";
import { ScrollToTop } from "@bindeleddet/core/components";
import { NavMenu as AdminNavMenu, NAVMENU_WIDTH } from "components/NavAdmin/NavMenu";
import { NavMenu as CompanyNavMenu } from "containers/Company/CompanyProfile/Menu/NavMenu";
import { Footer as MainFooter } from "containers/Footer/Footer";
import { useViewport } from "@bindeleddet/core/hooks";

// Containers
import NotFound404 from "components/ErrorPage/NotFound404/NotFound404";
import AboutUs from "containers/AboutUs/AboutUs";
import AdminPage from "containers/Admin/AdminPage";
import Articles from "containers/Articles/Articles";
import Casebank from "containers/Casebank/Casebank";
import CompanyProfilePage from "containers/Company/CompanyProfile/CompanyProfile";
import EventBooking from "containers/Company/EventBooking/EventBooking";
import EventBookingInfo from "containers/Company/EventBookingInfo/EventBookingInfo";
import ContactUs from "containers/ContactUs/ContactUs";
import CreateCompanyProfile from "containers/CreateCompanyProfile/CreateCompanyProfile";
import CreateStudentProfile from "containers/CreateStudentProfile/CreateStudentProfile";
import Events from "containers/Events/Events";
import FrontPage from "containers/FrontPage/FrontPage";
import JobPostings from "containers/JobPostings/JobPostings";
import Companies from "containers/Companies/Companies";
import PrivacyPolicy from "containers/PrivacyPolicy/PrivacyPolicy";
import Publications from "containers/Publications/Publications";
import SingleArticle from "containers/SingleArticle/SingleArticle";
import SingleCompany from "containers/SingleCompany/SingleCompany";
import SingleEventPage from "containers/SingleEventPage/SingleEventPage";
import SingleJobPosting from "containers/SingleJobPosting/SingleJobPosting";
import StudentProfilePage from "containers/StudentProfilePage/StudentProfilePage";
import StudentResetPassword from "containers/StudentResetPassword/StudentResetPassword";
import UserTerms from "containers/UserTerms/UserTerms";
import UserResetPassword from "containers/UserResetPassword/UserResetPassword";

// Error Pages
import Forbidden403NotBL from "components/ErrorPage/Forbidden403/Forbidden403NotBL";
import Forbidden403NotCompany from "components/ErrorPage/Forbidden403/Forbidden403NotCompany";
import Forbidden403NotStudent from "components/ErrorPage/Forbidden403/Forbidden403NotStudent";

import "./App.less";

dayjs.locale("nb");

const { Header, Footer, Sider, Content } = Layout;

// antd's cssinjs pipeline runs actual color-blend math (FastColor) on a small set of
// "hue seed" tokens - colorTextBase/colorBgBase (used to derive the whole neutral/fill
// palette) and colorPrimary/colorLink (each run through generateColorPalettes to derive
// their own hover/active shades, even though we also override those shades below). A
// var(--xxx) string can't survive that math and silently resolves to near-black. Every
// other token is used as a direct CSS value with no derivation, so it stays a CSS custom
// property and keeps tracking the live [data-theme] attribute without needing the theme
// object recomputed.
const THEME_SEED_COLORS: Record<ThemeMode, { textBase: string; bgBase: string; primary: string }> = {
  light: { textBase: "#050505", bgBase: "#fefefe", primary: "#0a283f" },
  dark: { textBase: "#efefef", bgBase: "#13222a", primary: "#0a283f" },
  kd: { textBase: "#050505", bgBase: "#ffffff", primary: "#350c78" },
};

// Equivalent of the antd v4 Less `modifyVars` theme overrides this app used to ship
// (see git history of craco.config.js).
const buildThemeConfig = (mode: ThemeMode): ThemeConfig => {
  const seed = THEME_SEED_COLORS[mode];
  return {
    // Without this, every derived token (colorFill family, shadows, etc.) is computed
    // with the light-mode blend formulas even in dark mode, since a dark colorBgBase
    // alone doesn't switch which algorithm antd uses to derive the rest.
    algorithm: mode === "dark" ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
    token: {
      colorTextBase: seed.textBase,
      colorBgBase: seed.bgBase,
      colorPrimary: seed.primary,
      // colorLink must be real too (see comment above), even though colorLinkHover/
      // Active below are plain var() overrides that don't need it.
      colorLink: seed.textBase,
      // Also real (not var(--neutral)): several components composite colors onto
      // colorBgContainer via FastColor.onBackground() (e.g. Empty's "simple" image),
      // which needs an actual color to blend against, not a var() reference. Equals
      // var(--neutral) in every theme already, so this isn't a value change.
      colorBgContainer: seed.bgBase,
      colorBgElevated: seed.bgBase,
      colorBgLayout: "var(--neutral-alt)",
      colorBorder: "var(--neutral-alter)",
      colorBorderSecondary: "var(--neutral-alter)",
      colorSplit: "var(--neutral-alter)",
      colorText: "var(--font-color)",
      colorTextSecondary: "var(--font-color-alt)",
      colorTextHeading: "var(--font-color)",
      borderRadius: 8,
      colorLinkHover: "var(--font-color-alt)",
      colorLinkActive: "var(--font-color-alt)",
      colorTextDisabled: "gray",
      fontFamily:
        '"Poppins", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
    },
    components: {
      Menu: {
        itemActiveBg: "var(--primary-color)",
        itemHoverBg: "var(--neutral-alt)",
        subMenuItemBg: "var(--neutral-alter)",
        // Same class of issue as Tabs above: these default to the fixed navy
        // colorPrimary, which sits directly on the page background with no fill of
        // its own (e.g. a SubMenu title when one of its children is selected) and
        // needs the theme-flipping accent to stay visible in dark mode.
        itemSelectedColor: "var(--bindeleddet-color)",
        subMenuItemSelectedColor: "var(--bindeleddet-color)",
      },
      Input: {
        addonBg: "var(--neutral-alt)",
      },
      Select: {
        optionActiveBg: "var(--neutral-alter)",
      },
      Checkbox: {
        borderRadiusSM: 4,
        // Match the page's own dark background rather than the (lighter) card/container
        // shade var(--neutral) that colorBgContainer defaults to.
        colorBgContainer: "var(--neutral-alt)",
      },
      Radio: {
        buttonBg: "var(--neutral-alt)",
        buttonCheckedBg: "var(--neutral-alt)",
        colorBgContainer: "var(--neutral-alt)",
      },
      Table: {
        // Not a CSS var in index.css either (v4's Less config hardcoded this exact
        // value too), so it's not part of the "nearly no hex" scope.
        borderColor: "#888888",
        headerBg: "var(--neutral-alt)",
        bodySortBg: "var(--neutral-alt)",
        rowHoverBg: "var(--neutral-alter)",
        // v5's default header cell padding reads as noticeably bulkier than v4's did.
        cellPaddingBlock: 12,
      },
      Tooltip: {
        colorTextLightSolid: "var(--light)",
      },
      // v5 adds a drop-shadow under buttons by default; v4 never had this, and it reads
      // as a stray grey smudge against this app's flat design, so drop it entirely.
      Button: {
        defaultShadow: "none",
        primaryShadow: "none",
        dangerShadow: "none",
      },
      Divider: {
        colorSplit: "var(--divider-color)",
      },
      Collapse: {
        headerBg: "var(--neutral-alt)",
        contentBg: "var(--neutral-alt)",
      },
      // Unlike Checkbox/Radio's checked fill (which paints its own background, so the
      // fixed navy colorPrimary reads fine in both themes), the Tabs ink bar and
      // selected-tab text sit directly on the page background with no fill of their
      // own - they need the theme-flipping accent color to stay visible in dark mode.
      Tabs: {
        inkBarColor: "var(--bindeleddet-color)",
        itemSelectedColor: "var(--bindeleddet-color)",
      },
    },
  };
};

function App() {
  const isBrowserDefaultDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
  const { width } = useViewport();

  const [firstPath, setFirstPath] = useState(window.location.pathname.split("/")[1]);
  const [secondPath, setSecondPath] = useState(window.location.pathname.split("/")[2]);
  const { currentUser, isLoadingCurrentUser } = useLoginContext();
  const isBlMember = isBlMemberUser(currentUser);
  const isCompany = isCompanyUser(currentUser);

  const defaultTheme = useMemo(() => {
    const browserDefault = isBrowserDefaultDark ? "dark" : "light";
    const localStorageTheme = localStorage.getItem("theme");
    if (firstPath === "kd") {
      return "kd";
    }
    if (localStorageTheme !== "dark" && localStorageTheme !== "light") {
      localStorage.removeItem("theme");
      return browserDefault;
    }
    return localStorageTheme || browserDefault;
  }, [isBrowserDefaultDark, firstPath]);

  const [theme, _setTheme] = useState<ThemeMode>(defaultTheme);

  const setTheme = useCallback((newTheme: ThemeMode, storePreference = false) => {
    if (storePreference) {
      localStorage.setItem("theme", newTheme);
    }
    _setTheme(newTheme);
  }, []);

  useEffect(
    function setDefaultTheme() {
      setTheme(defaultTheme);
    },
    [defaultTheme, setTheme]
  );
  document.body.setAttribute("data-theme", theme);

  const themeConfig = useMemo(() => buildThemeConfig(theme), [theme]);

  const checkUrl = useCallback(() => {
    const paths = window.location.pathname.split("/");
    setFirstPath(paths[1] || "");
    setSecondPath(paths[2] || "");
  }, []);

  useEffect(() => {
    checkUrl();
    window.addEventListener("popstate", checkUrl);
    window.addEventListener("pushState", checkUrl);
    window.addEventListener("click", checkUrl);

    return () => {
      window.removeEventListener("popstate", checkUrl);
      window.removeEventListener("pushState", checkUrl);
      window.removeEventListener("click", checkUrl);
    };
  }, [checkUrl]);

  const loadingComponent = (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: `calc(100vh - ${NAVBAR_HEIGHT}px)`,
      }}
    >
      <Spin size="large" />
    </div>
  );

  return (
    <ConfigProvider theme={themeConfig}>
      <AntApp>
        <NotificationBridge />
        <Router>
          <ThemeContext.Provider value={{ theme, setTheme }}>
            <Layout>
              {firstPath === "admin" && isBlMember && width >= 768 && (
                <Sider width={NAVMENU_WIDTH} style={{ background: "#fff" }}>
                  <AdminNavMenu />
                </Sider>
              )}
              <Layout style={{ padding: 0, minHeight: "100vh", background: "var(--neutral-alt)" }}>
                <Header style={{ position: "absolute", background: "transparent", padding: 0, inset: 0 }}>
                  {firstPath === "kd" ? null : firstPath === "admin" && isBlMember ? <AdminNavbar /> : <Navbar />}
                </Header>
                {firstPath === "company" && secondPath === "company-profile" && isCompany && width >= 768 && (
                  <Sider
                    width={270}
                    style={{
                      overflow: "auto",
                      left: 0,
                      top: 0,
                      bottom: 0,
                      paddingTop: 50,
                      background: "var(--neutral)",
                      boxShadow: "1px 0px 5px rgba(0, 0, 0, 0.1)",
                    }}
                  >
                    <CompanyNavMenu company={currentUser} />
                  </Sider>
                )}
                <Content
                  style={{
                    margin: 0,
                    paddingTop: firstPath === "" ? "0px" : `${NAVBAR_HEIGHT}px`,
                    paddingBottom: firstPath !== "kd" ? "48px" : "0px",
                    minHeight: 280,
                  }}
                >
                  <ScrollToTop />
                  <Switch>
                    {firstPath === "admin" && !isBlMember && !isLoadingCurrentUser && (
                      <Route component={Forbidden403NotBL} />
                    )}
                    {firstPath === "company" && !isCompany && !isLoadingCurrentUser && (
                      <Route component={Forbidden403NotCompany} />
                    )}
                    {firstPath === "student-profile" && (!currentUser || isCompany) && !isLoadingCurrentUser && (
                      <Route component={Forbidden403NotStudent} />
                    )}

                    {/* Admin Routes */}
                    <Route path="/admin/">{isLoadingCurrentUser ? loadingComponent : <AdminPage />}</Route>

                    {/* Public Routes */}
                    <Route exact path="/" component={FrontPage} />
                    <Route path="/events/:id/" component={SingleEventPage} />
                    <Route path="/user/resetpassword/" component={UserResetPassword} />
                    <Route path="/students/reset-password/:token/" component={StudentResetPassword} />
                    <Route path="/events/" component={Events} />
                    <Route path="/jobs/:id/" component={SingleJobPosting} />
                    <Route path="/jobs/" component={JobPostings} />
                    <Route path="/companies/:id/" component={SingleCompany} />
                    <Route path="/companies/" component={Companies} />
                    <Route path="/casebank/" component={Casebank} />
                    <Route path="/publications/" component={Publications} />
                    <Route exact path="/articles/" component={Articles} />
                    <Route path="/articles/:id/" component={SingleArticle} />
                    <Route path="/about-us/" component={AboutUs} />
                    <Route path="/contact-us/" component={ContactUs} />
                    <Route path="/privacy-policy/" component={PrivacyPolicy} />
                    <Route path="/user-terms/" component={UserTerms} />
                    <Route path="/create-student-profile/" component={CreateStudentProfile} />
                    <Route path="/create-company-profile/" component={CreateCompanyProfile} />
                    <Route path="/student-profile/">
                      {isLoadingCurrentUser ? loadingComponent : <StudentProfilePage />}
                    </Route>

                    {/* Company Routes */}
                    <Route path="/company/company-profile/">
                      {isLoadingCurrentUser ? loadingComponent : <CompanyProfilePage />}
                    </Route>
                    <Route path="/company/event-booking/booking/" component={EventBooking} />
                    <Route path="/company/event-booking/" component={EventBookingInfo} />
                    <Redirect from="/company/event-booking-info/" to="/company/event-booking/" />

                    {/* Fallback Route */}
                    <Route component={NotFound404} />
                  </Switch>
                </Content>
              </Layout>

              {/* Footer */}
              {!(firstPath === "admin" && isBlMember) && (
                <Footer style={{ padding: 0 }}>{firstPath === "kd" ? null : <MainFooter />}</Footer>
              )}
            </Layout>

            {/* Footer admin */}
            {firstPath === "admin" && isBlMember && (
              <Footer style={{ padding: 0 }}>
                <AdminFooter />
              </Footer>
            )}
          </ThemeContext.Provider>
        </Router>
      </AntApp>
    </ConfigProvider>
  );
}

export default App;
